import { motion, useReducedMotion } from "framer-motion";
import { useId } from "react";
import clsx from "clsx";

interface Props {
  size?: number;
  className?: string;
  /** Aria-label opcional para leitores de tela. Default: "Carregando" */
  label?: string;
}

/**
 * IagoSpinner — mini-cérebro neural firing usado como indicador de
 * "IAGO está pensando/processando". Versão simplificada do IagoAvatar:
 * apenas duas hemisferas com pulso de luz percorrendo as edges em
 * sentidos opostos.
 *
 * Drop-in para Loader2 — usar onde a operação envolve IA explícita
 * (transcrição, rescore, chat, geração). Para I/O bruto (save, delete,
 * auth) manter Loader2 neutro para preservar a semântica visual.
 */

// Sequência de arestas que o "pulso" percorre em cada hemisfério.
// Coordenadas no espaço 0..40 (viewBox compacto).
const LEFT_PATH: [number, number][] = [
  [20, 4], // topo
  [10, 12],
  [4, 22],
  [10, 32],
  [20, 36],
];
const RIGHT_PATH: [number, number][] = [
  [20, 4],
  [30, 12],
  [36, 22],
  [30, 32],
  [20, 36],
];

function pathString(pts: [number, number][]) {
  return pts.reduce(
    (acc, [x, y], i) => acc + (i === 0 ? `M ${x} ${y}` : ` L ${x} ${y}`),
    "",
  );
}

export default function IagoSpinner({ size = 20, className, label }: Props) {
  const reduce = useReducedMotion();
  const uid = useId().replace(/:/g, "-");
  const idLeft = `iago-sp-l-${uid}`;
  const idRight = `iago-sp-r-${uid}`;

  return (
    <span
      className={clsx("inline-flex items-center justify-center", className)}
      role="status"
      aria-label={label ?? "Carregando"}
      style={{ width: size, height: size }}
    >
      <svg
        viewBox="0 0 40 40"
        width={size}
        height={size}
        style={{ display: "block", overflow: "visible" }}
        aria-hidden
      >
        <defs>
          <linearGradient id={idLeft} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#f0abfc" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
          <linearGradient id={idRight} x1="100%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#7dd3fc" />
            <stop offset="100%" stopColor="#8b5cf6" />
          </linearGradient>
        </defs>

        {/* halo radial sutil */}
        <circle
          cx="20"
          cy="20"
          r="18"
          fill="url(#__none)"
          stroke="rgba(217,70,239,0.12)"
          strokeWidth="1"
        />

        {/* trilhos das hemisferas (estáticos, sutis) */}
        <path
          d={pathString(LEFT_PATH)}
          fill="none"
          stroke={`url(#${idLeft})`}
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.32"
        />
        <path
          d={pathString(RIGHT_PATH)}
          fill="none"
          stroke={`url(#${idRight})`}
          strokeWidth="1.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity="0.32"
        />

        {/* pulsos animados — luz percorrendo as edges em sentidos opostos */}
        {!reduce && (
          <>
            <PulseDot
              path={LEFT_PATH}
              color="#fdf4ff"
              shadow="rgba(217,70,239,1)"
              duration={1.2}
              delay={0}
            />
            <PulseDot
              path={[...RIGHT_PATH].reverse()}
              color="#f0f9ff"
              shadow="rgba(14,165,233,1)"
              duration={1.2}
              delay={0.6}
            />
          </>
        )}

        {/* nós nos vértices das hemisferas */}
        {LEFT_PATH.map(([x, y], i) => (
          <circle
            key={`l-${i}`}
            cx={x}
            cy={y}
            r="1"
            fill="#f0abfc"
            opacity="0.85"
          />
        ))}
        {RIGHT_PATH.map(([x, y], i) => (
          <circle
            key={`r-${i}`}
            cx={x}
            cy={y}
            r="1"
            fill="#7dd3fc"
            opacity="0.85"
          />
        ))}

        {/* fissura central */}
        <line
          x1="20"
          y1="4"
          x2="20"
          y2="36"
          stroke="rgba(255,255,255,0.18)"
          strokeWidth="0.5"
          strokeDasharray="1.2 1.2"
        />
      </svg>
    </span>
  );
}

function PulseDot({
  path,
  color,
  shadow,
  duration,
  delay,
}: {
  path: [number, number][];
  color: string;
  shadow: string;
  duration: number;
  delay: number;
}) {
  // Anima cx/cy seguindo cada vértice em sequência, suavizado por keyTimes.
  const xs = path.map(([x]) => x);
  const ys = path.map(([, y]) => y);
  return (
    <motion.circle
      r="1.6"
      fill={color}
      initial={{ cx: xs[0], cy: ys[0] }}
      animate={{ cx: xs, cy: ys }}
      transition={{
        duration,
        delay,
        repeat: Infinity,
        repeatDelay: 0.2,
        ease: "easeInOut",
        times: xs.map((_, i) => i / (xs.length - 1)),
      }}
      style={{ filter: `drop-shadow(0 0 3px ${shadow})` }}
    />
  );
}
